// src/features/quotation/components/QuotationList.jsx
import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Tooltip,
  Menu,
  MenuItem,
  LinearProgress,
  Chip,
} from "@mui/material";
import {
  Visibility,
  Edit,
  Delete,
  Send,
  MoreVert,
  CheckCircle,
  Cancel,
  Pending,
  Receipt,
  Print,
} from "@mui/icons-material";
import {
  formatCurrency,
  getStatusLabel,
  getStatusColor,
} from "../utils/quotationHelpers";
import QuotationStatusBadge from "./QuotationStatusBadge";

const QuotationList = ({
  quotations = [],
  loading = false,
  onEdit,
  onDelete,
  onStatusChange,
  onSend,
  onPreview,
  onConvert,
  onPrint,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedQuotation, setSelectedQuotation] = useState(null);

  const handleMenuOpen = (event, quotation) => {
    setAnchorEl(event.currentTarget);
    setSelectedQuotation(quotation);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedQuotation(null);
  };

  const handleAction = (action) => {
    if (!selectedQuotation) return;

    switch (action) {
      case "view":
        onPreview && onPreview(selectedQuotation);
        break;
      case "edit":
        onEdit && onEdit(selectedQuotation);
        break;
      case "delete":
        onDelete && onDelete(selectedQuotation.id);
        break;
      case "send":
        onSend && onSend(selectedQuotation.id);
        break;
      case "approve":
        onStatusChange && onStatusChange(selectedQuotation.id, "approved");
        break;
      case "reject":
        onStatusChange && onStatusChange(selectedQuotation.id, "rejected");
        break;
      case "convert":
        onConvert && onConvert(selectedQuotation.id);
        break;
      case "print":
        onPrint && onPrint(selectedQuotation);
        break;
      default:
        break;
    }
    handleMenuClose();
  };

  const canEdit = (status) => {
    return ["draft"].includes(status);
  };

  const canSend = (status) => {
    return ["draft"].includes(status);
  };

  const canApprove = (status) => {
    return ["sent", "viewed"].includes(status);
  };

  const canReject = (status) => {
    return ["sent", "viewed"].includes(status);
  };

  const canConvert = (status) => {
    return status === "approved";
  };

  const getActions = (quotation) => {
    const actions = [];

    actions.push({
      key: "view",
      label: "View",
      icon: <Visibility fontSize="small" />,
    });

    if (canEdit(quotation.status)) {
      actions.push({
        key: "edit",
        label: "Edit",
        icon: <Edit fontSize="small" />,
      });
    }

    if (canSend(quotation.status)) {
      actions.push({
        key: "send",
        label: "Send to Client",
        icon: <Send fontSize="small" />,
      });
    }

    if (canApprove(quotation.status)) {
      actions.push({
        key: "approve",
        label: "Approve",
        icon: <CheckCircle fontSize="small" />,
      });
    }

    if (canReject(quotation.status)) {
      actions.push({
        key: "reject",
        label: "Reject",
        icon: <Cancel fontSize="small" />,
      });
    }

    if (canConvert(quotation.status) && !quotation.isConverted) {
      actions.push({
        key: "convert",
        label: "Convert to Invoice",
        icon: <Receipt fontSize="small" />,
      });
    }

    actions.push({
      key: "print",
      label: "Print",
      icon: <Print fontSize="small" />,
    });
    actions.push({
      key: "delete",
      label: "Delete",
      icon: <Delete fontSize="small" />,
      danger: true,
    });

    return actions;
  };

  if (loading) {
    return <LinearProgress sx={{ mt: 2 }} />;
  }

  if (!quotations || quotations.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No quotations found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create your first quotation to get started
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: "#f5f5f5" }}>
            <TableCell>Quotation #</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Client</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Valid Until</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {quotations.map((quotation) => (
            <TableRow key={quotation.id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight="bold">
                  {quotation.number}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                  {quotation.title || "Untitled"}
                </Typography>
              </TableCell>
              <TableCell>{quotation.clientName}</TableCell>
              <TableCell>
                {new Date(quotation.date).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <Typography
                  color={
                    new Date(quotation.validUntil) < new Date() &&
                    quotation.status !== "approved"
                      ? "error"
                      : "text.primary"
                  }
                >
                  {new Date(quotation.validUntil).toLocaleDateString()}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" fontWeight="bold">
                  {formatCurrency(quotation.total, quotation.currency)}
                </Typography>
              </TableCell>
              <TableCell>
                <QuotationStatusBadge status={quotation.status} />
              </TableCell>
              <TableCell align="center">
                <Box
                  sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}
                >
                  <Tooltip title="View">
                    <IconButton
                      size="small"
                      onClick={() => onPreview && onPreview(quotation)}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {canEdit(quotation.status) && (
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => onEdit && onEdit(quotation)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {canSend(quotation.status) && (
                    <Tooltip title="Send">
                      <IconButton
                        size="small"
                        onClick={() => onSend && onSend(quotation.id)}
                        color="primary"
                      >
                        <Send fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {canConvert(quotation.status) && !quotation.isConverted && (
                    <Tooltip title="Convert to Invoice">
                      <IconButton
                        size="small"
                        onClick={() => onConvert && onConvert(quotation.id)}
                        color="success"
                      >
                        <Receipt fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  <Tooltip title="More">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, quotation)}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedQuotation &&
          getActions(selectedQuotation).map((action) => (
            <MenuItem
              key={action.key}
              onClick={() => handleAction(action.key)}
              sx={{
                color: action.danger ? "error.main" : "inherit",
              }}
            >
              {action.icon}
              <Typography variant="body2" sx={{ ml: 1 }}>
                {action.label}
              </Typography>
            </MenuItem>
          ))}
      </Menu>
    </TableContainer>
  );
};

export default QuotationList;
